import React, { ReactElement, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ClientCompanyProduct, SprintProductStatus } from '@data';
import { fetchPmSprintProducts } from '@data/api/sprintProducts';
import * as queryKeys from '@data/constants/keys';
import { Loader } from '@ui';
import { Pagination } from '@app/components/core/pagination';
import { SectionHeading } from '@app/components/core/cards';
import { limitConcurrency } from '@app/utils/helpers/limitConcurrency';

import WorkforceGrid, { type SprintHours } from '../../products/productDetail/WorkforceGrid';
import { FIRST_MONEY_YEAR, noSprintsKey, useYearChoice } from '../../products/productDetail/yearChoice';
import { useProductRevenue } from '../../insights/productRevenue';
import { openForClient } from './openForClient';
import { useClientIncidentCosts } from './clientIncidentCosts';
import { NO_PRIORITY, incidentRowId } from './incidentFigures';

const SETTLED = [SprintProductStatus.closed, SprintProductStatus.approved, SprintProductStatus.invoiced];

/** Per product: the org-wide list keeps its backlog cutoff unless it is asked about one product. */
const useClosedForAll = (
  organizationId: string,
  productIds: string[],
): { closedByProduct: Record<string, string[]>; isLoading: boolean; isError: boolean } => {
  const queue = useMemo(() => limitConcurrency(4), []);
  const queries = useQueries({
    queries: productIds.map(productId => {
      const params = { statuses: SETTLED, wholeHistory: true, productId };
      return {
        queryKey: [queryKeys.PM_SPRINT_PRODUCTS_LIST, { organizationId, params }],
        queryFn: () => queue(() => fetchPmSprintProducts(organizationId, params)),
        enabled: !!organizationId,
      };
    }),
  });
  const stamps = queries.map(query => query.dataUpdatedAt).join(',');
  const closedByProduct = useMemo(
    () =>
      Object.fromEntries(
        productIds.map((productId, index) => [
          productId,
          (queries[index]?.data?.items ?? [])
            .map(item => item.sprintId ?? item.sprint?.id)
            .filter((id): id is string => !!id),
        ]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stamps],
  );
  return {
    closedByProduct,
    isLoading: queries.some(query => query.isLoading),
    isError: queries.some(query => query.isError),
  };
};

/** The client's products as rows, read as each product's own forecast reads them. */
const ClientCostTab = ({
  organizationId,
  clientId,
  products,
}: {
  organizationId: string;
  clientId: string;
  products: ClientCompanyProduct[];
}): ReactElement => {
  const { t } = useTranslation();
  const { sprints, years, year, choose, yearSprints, isLoading } = useYearChoice();
  const rows = useMemo(() => products.map(product => ({ id: product.id, name: product.name })), [products]);
  const { revenue, isLoading: pricing, isError: pricingFailed } = useProductRevenue(rows, yearSprints);
  const incidents = useClientIncidentCosts(rows, yearSprints);
  const closed = useClosedForAll(
    organizationId,
    rows.map(row => row.id),
  );

  if (isLoading) return <Loader />;
  if (!products.length) return <p className="text-sm text-muted-foreground">{t('clientCost.none')}</p>;
  if (!yearSprints.length) {
    return <p className="text-sm text-muted-foreground">{t(noSprintsKey(sprints), { year: FIRST_MONEY_YEAR })}</p>;
  }
  // Never a confident €0 on a failed read, and which sprints are closed decides every column.
  if (pricingFailed || closed.isError) return <p className="text-sm text-muted-foreground">{t('cardReadFailed')}</p>;
  if (closed.isLoading) return <Loader />;

  // A fixed price bills with no hours, so either figure counts as work.
  const worked = (product: (typeof revenue)[number], sprintId: string): boolean =>
    !!product.bySprint[sprintId] || !!product.hoursBySprint[sprintId];
  const sprintIds = yearSprints.map(sprint => sprint.id);
  const openSprintIds = openForClient(
    sprintIds,
    revenue.map(product => ({
      closed: closed.closedByProduct[product.id] ?? [],
      worked: sprintId => worked(product, sprintId),
    })),
  );
  // Spent once every product that worked in it has closed it; until then, and before anyone works in it, projected.
  const projectedIds = sprintIds.filter(
    id => openSprintIds.includes(id) || !revenue.some(product => worked(product, id)),
  );

  const costs: SprintHours = {};
  const hours: SprintHours = {};
  revenue.forEach(product => {
    yearSprints.forEach(sprint => {
      const projected = projectedIds.includes(sprint.id);
      const cost = projected ? product.projectedBySprint[sprint.id] : product.bySprint[sprint.id];
      const time = projected ? product.projectedHoursBySprint[sprint.id] : product.hoursBySprint[sprint.id];
      costs[sprint.id] = { ...costs[sprint.id], [product.id]: cost ?? 0 };
      hours[sprint.id] = { ...hours[sprint.id], [product.id]: time ?? 0 };
    });
  });

  const columns = yearSprints.map(sprint => ({
    id: sprint.id,
    number: sprint.number,
    startDate: sprint.startDate,
    endDate: sprint.endDate,
  }));
  // One block per product, folded to its total until opened.
  const incidentGroups = rows.map(row => ({
    key: row.id,
    label: row.name,
    people: (incidents.bucketsByProduct[row.id] ?? []).map(bucket => ({
      id: incidentRowId(row.id, bucket),
      name: bucket === NO_PRIORITY ? t('clientCost.noPriority') : bucket.toUpperCase(),
    })),
  }));

  return (
    <section>
      <SectionHeading>{t('clientCost.sectionTitle', { year })}</SectionHeading>
      <p className="mb-4 -mt-1 max-w-3xl text-sm text-muted-foreground">{t('clientCost.sectionIntro')}</p>
      <div className="space-y-6">
        <WorkforceGrid
          gridId={`client:${clientId}`}
          title={t('clientCost.title')}
          hint={pricing ? t('clientCost.pricing') : t('clientCost.hint')}
          nameLabel={t('titles.product')}
          groups={[{ key: '', label: '', people: rows }]}
          sprints={columns}
          hours={hours}
          costs={costs}
          lead="euros"
          totalColumn
          projectedSprintIds={projectedIds}
        />
        {/* Never a confident €0 on a failed read. */}
        {incidents.isError ? (
          <p className="text-sm text-muted-foreground">{t('cardReadFailed')}</p>
        ) : (
          <WorkforceGrid
            gridId={`client-incidents:${clientId}`}
            title={t('clientCost.incidentsTitle')}
            hint={incidents.isLoading ? t('clientCost.pricing') : t('clientCost.incidentsHint')}
            nameLabel={t('titles.product')}
            groups={incidentGroups}
            grouped
            sprints={columns}
            hours={incidents.hours}
            costs={incidents.costs}
            lead="euros"
            totalColumn
          />
        )}
        <Pagination
          page={years.indexOf(year)}
          pageCount={years.length}
          onChange={page => choose(years[page])}
          labels={years}
          reversed
        />
      </div>
    </section>
  );
};

export default ClientCostTab;
